import { FileText, X } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { DRAFT_SOURCE_DOC_LABELS, type DraftSourceDocType } from "@/types/drafting";
import { cn } from "@/lib/utils";

interface DraftSourceDocumentChipProps {
  docType: DraftSourceDocType | string;
  fileName: string;
  onRemove?: () => void;
  className?: string;
}

export function DraftSourceDocumentChip({
  docType,
  fileName,
  onRemove,
  className,
}: DraftSourceDocumentChipProps) {
  const typeLabel =
    DRAFT_SOURCE_DOC_LABELS[docType as DraftSourceDocType] ??
    String(docType).replace(/-/g, " ");

  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center gap-2 rounded-xl border border-[var(--lm-border-card)] bg-white px-2 py-1.5 shadow-[var(--shadow-card)] dark:border-slate-700/80 dark:bg-slate-900/55 dark:shadow-none",
        className
      )}
      title={fileName}
    >
      <FileText className="h-3.5 w-3.5 shrink-0 text-violet-500 dark:text-violet-300" aria-hidden />
      <span
        className="shrink-0 rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-500/15 dark:text-violet-200"
      >
        {typeLabel}
      </span>
      <span className="min-w-0 max-w-[12rem] truncate text-xs font-medium text-[var(--lm-foreground-strong)] sm:max-w-[16rem] dark:text-slate-100">
        {fileName}
      </span>
      {onRemove ? (
        <IconButton
          type="button"
          size="sm"
          variant="destructive"
          title="Remove"
          aria-label={`Remove ${fileName}`}
          className="ml-0.5 h-7 w-7 shrink-0"
          onClick={onRemove}
        >
          <X className="h-3.5 w-3.5" />
        </IconButton>
      ) : null}
    </div>
  );
}
